import {
  AlertTriangle,
  BookOpen,
  Check,
  CheckCircle2,
  Clipboard,
  Clock3,
  Code2,
  Copy,
  KeyRound,
  Loader2,
  LockKeyhole,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";

type ApiScope = "account:read" | "products:read" | "balances:read" | "stores:read" | "labels:validate" | "labels:write" | "labels:read";

type ApiKeySummary = {
  allowedIps: string[];
  createdAt: string;
  expiresAt: string | null;
  id: string;
  keyPrefix: string;
  labelLimitPerDay: number;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  maskedKey: string;
  name: string;
  rateLimitPerMinute: number;
  requestCount30d: number;
  revokedAt: string | null;
  scopes: ApiScope[];
  status: "active" | "expired" | "revoked";
  updatedAt: string;
  usageCount: number;
};

type KeysResponse = {
  availableScopes: ApiScope[];
  canManage: boolean;
  keys: ApiKeySummary[];
  limits: {
    accountLabelLimitPerDay: number;
    accountRateLimitPerMinute: number;
    defaultLabelLimitPerDay: number;
    labelRateLimitPerMinutePerAccount: number;
    labelRateLimitPerMinutePerKey: number;
    maxActiveKeys: number;
    maxAllowedIps: number;
    maxLabelLimitPerDay: number;
    maxRateLimitPerMinute: number;
    minLabelLimitPerDay: number;
    minRateLimitPerMinute: number;
  };
  ok: true;
};

const scopeDescriptions: Record<ApiScope, { label: string; detail: string }> = {
  "account:read": { label: "Hesap bilgisi", detail: "Hesap ve gönderici hazırlık durumunu okur." },
  "products:read": { label: "Etiket ürünleri", detail: "Kullanılabilir ürünleri ve ağırlıkları okur." },
  "balances:read": { label: "Bakiyeler", detail: "Etiket kredilerini okur." },
  "stores:read": { label: "Mağazalar", detail: "Bağlı mağazaları ve senkron durumunu salt okunur görüntüler." },
  "labels:validate": { label: "Adres doğrulama", detail: "Kredi harcamadan etiket isteğini kontrol eder." },
  "labels:write": { label: "Etiket oluşturma ve iptal", detail: "Manuel krediden gönderi/iade etiketi oluşturur ve uygun etiketleri iptal eder." },
  "labels:read": { label: "Etiket okuma", detail: "API etiketlerini ve PDF dosyalarını okur." },
};

const allScopes = Object.keys(scopeDescriptions) as ApiScope[];

function dateText(value: string | null) {
  if (!value) return "Henüz yok";
  const date = new Date(value);
  const language = typeof document === "undefined" ? "tr" : document.documentElement.lang;
  const locale = language === "de" ? "de-DE" : language === "en" ? "en-GB" : "tr-TR";
  return Number.isFinite(date.getTime()) ? date.toLocaleString(locale) : value;
}

async function jsonRequest<T>(path: string, options: RequestInit = {}) {
  const response = await fetch(path, {
    ...options,
    credentials: "same-origin",
    headers: options.body ? { "Content-Type": "application/json", ...(options.headers || {}) } : options.headers,
  });
  const payload = (await response.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!response.ok) throw new Error(payload?.error || "İstek tamamlanamadı.");
  return payload as T;
}

function statusMeta(status: ApiKeySummary["status"]) {
  if (status === "active") return { label: "Aktif", className: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  if (status === "expired") return { label: "Süresi doldu", className: "border-amber-200 bg-amber-50 text-amber-700" };
  return { label: "İptal edildi", className: "border-slate-200 bg-slate-100 text-slate-600" };
}

function CodeBlock({ children, onCopy }: { children: string; onCopy?: () => void }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 text-slate-100 shadow-sm">
      {onCopy ? (
        <button type="button" onClick={onCopy} className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/10 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/15">
          <Copy className="h-3.5 w-3.5" /> Kopyala
        </button>
      ) : null}
      <pre className="overflow-x-auto p-5 pr-24 text-xs leading-6"><code>{children}</code></pre>
    </div>
  );
}

function InfoCard({ icon, title, children }: { children: React.ReactNode; icon: React.ReactNode; title: string }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-slate-950 text-white">{icon}</span>
        <h2 className="text-base font-semibold text-slate-950">{title}</h2>
      </div>
      <div className="mt-4 text-sm leading-6 text-slate-600">{children}</div>
    </section>
  );
}

export function ApiAccessPage() {
  const pageLanguage = typeof document === "undefined" ? "tr" : document.documentElement.lang;
  const scenarioHelp = {
    tr: [
      { title: "İade etiketi oluşturma", body: "POST /labels çağrısında shipmentType: return gönderin. addresses kullanırsanız sender iadeyi gönderen müşteri, recipient seçtiğiniz iade deposudur; adresler ters çevrilmez. Eski recipient biçiminde müşteri gönderen, hesap profili alıcıdır. İade yeni bir etiket kredisi kullanır; ilk etiketi iptal etmez. Ayrı bir Idempotency-Key kullanın." },
      { title: "Gönderen ve alıcı adresini belirleme", body: "Normal gönderi ve iadede iki adresi de addresses.sender ve addresses.recipient içinde gönderin. İkisi de zorunludur; üst düzey recipient ile birlikte kullanmayın. Profil adresiniz değişmez ve bu biçimde profil adresi gerekmez. Önce /labels/validate ile normalizedAddresses ve labelAddress alanlarını kontrol edin. Etikete kaydedilen adresler yeniden oluşturmada korunur. Adres değiştirirseniz yeni Idempotency-Key kullanın." },
      { title: "Etiket iptali ve kredi iadesi", body: "POST /labels/{id}/cancel hem gönderi hem iade etiketinde kullanılabilir. Taşıyıcı hareketi varsa CARRIER_MOVEMENT_DETECTED ile reddedilir. Canlı kontrol alınamazsa paneldeki gibi kayıtlı hareket kontrol edilir. Tekrar iptal, alreadyCancelled: true ve creditRestored: false döner. İptal edilmiş PDF kullanılamaz; iade etiketi oluşturmak ayrı işlemdir." },
      { title: "Adres ve firma kuralları", body: "Önce POST /labels/validate kullanın. Packstation, Postfiliale ve Paketshop isim/adres/firma alanlarında engellenir. Kişi adı veya firma adı gerekir. Kapı numarası ve DHL satır sınırları kontrol edilir. Online Seller ön eki, firma satırları, karakter ve adres düzenlemeleri ortak PDF motoruyla uygulanır; errors, adjustments, normalizedRecipient ve labelAddress alanlarında sonucu görebilirsiniz." },
    ],
    en: [
      { title: "Create a return label", body: "Send shipmentType: return to POST /labels. With addresses, sender is the returning customer and recipient is your chosen return warehouse; parties are not reversed. In legacy recipient mode the customer sends to your account profile. A return uses a new label credit and does not cancel the original label. Use a separate Idempotency-Key." },
      { title: "Choose sender and recipient addresses", body: "For outbound and return labels, send both physical parties as addresses.sender and addresses.recipient. Both are required; do not combine them with top-level recipient. Your profile is neither changed nor required in this mode. Preview normalizedAddresses and labelAddress with /labels/validate. Saved addresses survive regeneration. Use a new Idempotency-Key when changing an address." },
      { title: "Cancel a label and restore credit", body: "POST /labels/{id}/cancel works for outbound and return labels. Carrier movement rejects cancellation with CARRIER_MOVEMENT_DETECTED. If live verification is unavailable, stored movement is checked as in the panel. A repeated cancellation returns alreadyCancelled: true and creditRestored: false. Cancelled PDFs cannot be used. Creating a return label is a separate operation." },
      { title: "Address and company rules", body: "Use POST /labels/validate first. Packstation, Postfiliale and Paketshop are blocked in name/address/company fields. A person or company name is required. House numbers and DHL line limits are checked. The shared PDF engine applies the Online Seller prefix, company lines, character and address formatting; inspect errors, adjustments, normalizedRecipient and labelAddress." },
    ],
    de: [
      { title: "Retourenlabel erstellen", body: "Senden Sie shipmentType: return an POST /labels. Mit addresses ist sender der zurücksendende Kunde und recipient das gewählte Retourenlager; die Adressen werden nicht vertauscht. Im bisherigen recipient-Modus sendet der Kunde an Ihr Kontoprofil. Ein Retourenlabel benötigt neues Guthaben und storniert das ursprüngliche Label nicht. Verwenden Sie einen eigenen Idempotency-Key." },
      { title: "Absender und Empfänger festlegen", body: "Übergeben Sie bei Versand und Retouren beide tatsächlichen Parteien mit addresses.sender und addresses.recipient. Beide sind erforderlich; nicht mit recipient auf oberster Ebene kombinieren. Das Kontoprofil wird weder geändert noch benötigt. Prüfen Sie normalizedAddresses und labelAddress mit /labels/validate. Gespeicherte Adressen bleiben bei Neuerstellung erhalten. Bei Adressänderungen einen neuen Idempotency-Key verwenden." },
      { title: "Label stornieren und Guthaben zurückgeben", body: "POST /labels/{id}/cancel gilt für Versand- und Retourenlabels. Bei Transportbewegungen wird die Stornierung mit CARRIER_MOVEMENT_DETECTED abgelehnt. Ist die Live-Prüfung nicht verfügbar, werden wie im Kundenbereich gespeicherte Bewegungen geprüft. Eine Wiederholung liefert alreadyCancelled: true und creditRestored: false. Stornierte PDFs dürfen nicht verwendet werden. Ein Retourenlabel ist ein separater Vorgang." },
      { title: "Adress- und Firmenregeln", body: "Nutzen Sie zuerst POST /labels/validate. Packstation, Postfiliale und Paketshop werden in Namens-, Adress- und Firmenfeldern gesperrt. Ein Personen- oder Firmenname ist erforderlich. Hausnummer und DHL-Zeilengrenzen werden geprüft. Die gemeinsame PDF-Erstellung berücksichtigt Online Seller, Firmenzeilen sowie Zeichen- und Adressformatierung; prüfen Sie errors, adjustments, normalizedRecipient und labelAddress." },
    ],
  }[pageLanguage === "de" ? "de" : pageLanguage === "en" ? "en" : "tr"];
  const insufficientCreditExample = pageLanguage === "de"
    ? "Für das ausgewählte Produkt ist kein Label-Guthaben verfügbar."
    : pageLanguage === "en"
      ? "No label credit is available for the selected product."
      : "Seçilen ürün için kullanılabilir etiket kredisi yok.";
  const [data, setData] = useState<KeysResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState("");
  const [secret, setSecret] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);
  const [name, setName] = useState("Canlı API");
  const [selectedScopes, setSelectedScopes] = useState<ApiScope[]>(allScopes);
  const [allowedIps, setAllowedIps] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [rateLimit, setRateLimit] = useState(120);
  const [labelLimitPerDay, setLabelLimitPerDay] = useState(100);
  const baseUrl = useMemo(() => (typeof window === "undefined" ? "https://app.kenpost.de/v1" : `${window.location.origin}/v1`), []);

  async function load() {
    setLoading(true);
    setError("");
    try {
      setData(await jsonRequest<KeysResponse>("/api/api-keys"));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function copyText(value: string, message = "Panoya kopyalandı.") {
    await navigator.clipboard.writeText(value);
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2500);
  }

  async function createKey(event: FormEvent) {
    event.preventDefault();
    if (creating) return;
    setCreating(true);
    setError("");
    try {
      const result = await jsonRequest<{ apiKey: ApiKeySummary; ok: true; secret: string; warning: string }>("/api/api-keys", {
        method: "POST",
        body: JSON.stringify({
          name,
          scopes: selectedScopes,
          allowedIps: allowedIps.split(/[\s,;]+/).filter(Boolean),
          expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
          labelLimitPerDay,
          rateLimitPerMinute: rateLimit,
        }),
      });
      setSecret(result.secret);
      setSecretCopied(false);
      setShowCreate(false);
      setNotice("API anahtarı oluşturuldu. Şimdi güvenli bir yere kaydedin.");
      await load();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : String(createError));
    } finally {
      setCreating(false);
    }
  }

  async function revokeKey(key: ApiKeySummary) {
    if (key.status !== "active" || !window.confirm(`“${key.name}” API anahtarını iptal etmek istiyor musunuz? Bu işlem geri alınamaz.`)) return;
    setRevokingId(key.id);
    setError("");
    try {
      await jsonRequest<{ ok: true }>(`/api/api-keys/${encodeURIComponent(key.id)}`, { method: "DELETE" });
      setNotice("API anahtarı iptal edildi.");
      await load();
    } catch (revokeError) {
      setError(revokeError instanceof Error ? revokeError.message : String(revokeError));
    } finally {
      setRevokingId("");
    }
  }

  const curlExample = `curl -X POST "${baseUrl}/labels" \\
  -H "Authorization: Bearer <API_KEY>" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: order-10042-package-1" \\
  -d '{
    "labelProductId": "<LABEL_PRODUCT_ID>",
    "reference": "ORDER-10042",
    "shipmentType": "outbound",
    "recipient": {
      "name": "Max Mustermann",
      "company": "",
      "address1": "Musterstr. 12",
      "address2": "",
      "postalCode": "10115",
      "city": "Berlin",
      "countryCode": "DE",
      "email": "max@example.de",
      "phone": "+491701234567"
    }
  }'`;
  const explicitAddressExample = (shipmentType: "outbound" | "return") => {
    const warehouse = { name: "Versandlager", address1: "Lagerstr. 45", postalCode: "80331", city: "München", countryCode: "DE" };
    const customer = { name: "Max Mustermann", address1: "Musterstr. 12", postalCode: "10115", city: "Berlin", countryCode: "DE" };
    const body = JSON.stringify({ labelProductId: "<LABEL_PRODUCT_ID>", reference: `ORDER-10042-${shipmentType}`, shipmentType,
      addresses: shipmentType === "return" ? { sender: customer, recipient: warehouse } : { sender: warehouse, recipient: customer } }, null, 2);
    const continuation = String.fromCharCode(92);
    return [`curl -X POST "${baseUrl}/labels" ${continuation}`,
      `  -H "Authorization: Bearer <API_KEY>" ${continuation}`,
      `  -H "Content-Type: application/json" ${continuation}`,
      `  -H "Idempotency-Key: order-10042-${shipmentType}-custom-address" ${continuation}`,
      `  -d '${body}'`].join("\n");
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-slate-950 text-white shadow-sm"><KeyRound className="h-5 w-5" /></span>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-950">API</h1>
              <p className="mt-1 text-sm text-slate-500">Kendi yazılımınızdan KenPost etiketi oluşturun ve sonuçları güvenle alın.</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => void load()} disabled={loading} className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Yenile
          </button>
          <button type="button" onClick={() => setShowCreate(true)} disabled={data ? !data.canManage : true} title={data && !data.canManage ? "API anahtarı yalnızca hesap sahibinin kendi oturumunda oluşturulabilir." : undefined} className="inline-flex h-11 items-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-45">
            <Plus className="h-4 w-4" /> API key oluştur
          </button>
        </div>
      </header>

      {notice ? <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800"><CheckCircle2 className="h-4 w-4" />{notice}</div> : null}
      {error ? <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}</div> : null}
      {data && !data.canManage ? <div className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />API anahtarı yalnızca hesap sahibinin kendi oturumunda oluşturulabilir veya iptal edilebilir.</div> : null}

      {secret ? (
        <section className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-amber-950"><LockKeyhole className="h-5 w-5" /><h2 className="font-semibold">Yeni API anahtarınız</h2></div>
              <p className="mt-2 text-sm text-amber-800">Bu değer yalnızca bir kez gösterilir. Şimdi kopyalayıp parola kasanıza kaydedin.</p>
            </div>
            <button type="button" onClick={() => setSecret("")} className="rounded-lg p-1 text-amber-700 hover:bg-amber-100" aria-label="Kapat"><X className="h-5 w-5" /></button>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <code className="min-w-0 flex-1 overflow-x-auto rounded-xl border border-amber-200 bg-white px-4 py-3 text-xs font-semibold text-slate-900">{secret}</code>
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(secret);
                setSecretCopied(true);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-950 px-4 py-3 text-sm font-semibold text-white"
            >
              {secretCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {secretCopied ? "Kopyalandı" : "Anahtarı kopyala"}
            </button>
          </div>
        </section>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <InfoCard icon={<ShieldCheck className="h-5 w-5" />} title="Güvenli erişim">
          Anahtarlar yalnızca bir kez gösterilir ve KenPost'ta geri okunamaz. Her anahtarı ayrı yetki, IP, son kullanma tarihi ve günlük etiket kotasıyla sınırlandırabilirsiniz.
        </InfoCard>
        <InfoCard icon={<RefreshCw className="h-5 w-5" />} title="Tekrarsız işlem">
          Etiket oluştururken <code className="rounded bg-slate-100 px-1.5 py-0.5">Idempotency-Key</code> zorunludur. Ağ hatasında aynı istek tekrar edilse bile ikinci kredi harcanmaz.
        </InfoCard>
        <InfoCard icon={<Code2 className="h-5 w-5" />} title="Sunucudan sunucuya">
          API yalnızca arka uç yazılımları içindir. Mağaza bağlama ve entegrasyon ayarları güvenlik nedeniyle müşteri panelinde kalır.
        </InfoCard>
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="font-semibold text-slate-950">API anahtarları</h2>
            <p className="mt-1 text-xs text-slate-500">Son kullanım ve istek sayıları burada izlenir.</p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{data?.keys.length || 0} anahtar</span>
        </div>
        {loading && !data ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Anahtarlar yükleniyor</div>
        ) : data?.keys.length ? (
          <div className="divide-y divide-slate-100">
            {data.keys.map((key) => {
              const status = statusMeta(key.status);
              return (
                <article key={key.id} className="p-5">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-slate-950">{key.name}</h3>
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${status.className}`}>{status.label}</span>
                      </div>
                      <code className="mt-2 block text-xs text-slate-500">{key.maskedKey}</code>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {key.scopes.map((scope) => <span key={scope} className="rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600">{scope}</span>)}
                      </div>
                    </div>
                    <div className="grid gap-x-8 gap-y-2 text-xs text-slate-500 sm:grid-cols-2 xl:min-w-[470px]">
                      <p><span className="font-semibold text-slate-700">Son kullanım:</span> {dateText(key.lastUsedAt)}</p>
                      <p><span className="font-semibold text-slate-700">Son IP:</span> {key.lastUsedIp || "Henüz yok"}</p>
                      <p><span className="font-semibold text-slate-700">30 günlük istek:</span> {key.requestCount30d.toLocaleString("tr-TR")}</p>
                      <p><span className="font-semibold text-slate-700">Hız sınırı:</span> {key.rateLimitPerMinute}/dakika</p>
                      <p><span className="font-semibold text-slate-700">Etiket kotası:</span> {key.labelLimitPerDay}/gün</p>
                      <p><span className="font-semibold text-slate-700">Oluşturma:</span> {dateText(key.createdAt)}</p>
                      <p><span className="font-semibold text-slate-700">Son kullanma:</span> {dateText(key.expiresAt)}</p>
                      <p className="sm:col-span-2"><span className="font-semibold text-slate-700">IP kısıtı:</span> {key.allowedIps.length ? key.allowedIps.join(", ") : "Yok"}</p>
                    </div>
                    <button type="button" onClick={() => void revokeKey(key)} disabled={!data.canManage || key.status !== "active" || revokingId === key.id} className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 text-xs font-semibold text-red-700 disabled:cursor-not-allowed disabled:opacity-40">
                      {revokingId === key.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} İptal et
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="p-10 text-center"><KeyRound className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-3 text-sm font-medium text-slate-700">Henüz API anahtarı yok.</p><p className="mt-1 text-xs text-slate-500">İlk entegrasyonunuz için ayrı bir anahtar oluşturun.</p></div>
        )}
      </section>

      <section className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-indigo-50 text-indigo-700"><BookOpen className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1"><h2 className="text-lg font-semibold text-slate-950">API dokümantasyonu</h2><p className="mt-1 break-all text-sm text-slate-500">Sürüm 1 · Temel adres: <code>{baseUrl}</code></p></div>
          <a href="/api/kenpost-openapi.json" download className="shrink-0 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700">OpenAPI indir</a>
        </div>

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          Tüm isteklerde <code className="font-semibold">Authorization: Bearer &lt;API_KEY&gt;</code> başlığı kullanılır. Anahtarı URL'ye, tarayıcı koduna veya loglara yazmayın.
        </div>

        {data ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Anahtar sınırı</p><p className="mt-1 text-lg font-semibold text-slate-950">Ayarlanan değer / dakika</p><p className="mt-1 text-xs leading-5 text-slate-500">Her anahtar ayrı sayılır; hesap toplamı en fazla {data.limits.accountRateLimitPerMinute}/dakikadır.</p></div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Etiket dakika sınırı</p><p className="mt-1 text-lg font-semibold text-slate-950">{data.limits.labelRateLimitPerMinutePerKey} / anahtar</p><p className="mt-1 text-xs leading-5 text-slate-500">Tüm anahtarlar birlikte en fazla {data.limits.labelRateLimitPerMinutePerAccount} etiket isteği/dakika.</p></div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Günlük etiket kotası</p><p className="mt-1 text-lg font-semibold text-slate-950">Anahtar başına ayarlanır</p><p className="mt-1 text-xs leading-5 text-slate-500">Varsayılan {data.limits.defaultLabelLimitPerDay}, en fazla {data.limits.maxLabelLimitPerDay}/gün.</p></div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hesap güvenlik tavanı</p><p className="mt-1 text-lg font-semibold text-slate-950">{data.limits.accountLabelLimitPerDay}/gün</p><p className="mt-1 text-xs leading-5 text-slate-500">Birden çok anahtar kullanılsa bile hesap genelinde aşılmaz.</p></div>
          </div>
        ) : null}

        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Metot</th><th className="px-4 py-3">Uç</th><th className="px-4 py-3">Yetki</th><th className="px-4 py-3">Açıklama</th></tr></thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {[
                ["GET", "/account", "account:read", "Hesap ve gönderici hazırlığı"],
                ["GET", "/capabilities", "account:read", "Desteklenen özellikler"],
                ["GET", "/label-products", "products:read", "Etiket ürünleri"],
                ["GET", "/balances", "balances:read", "Kullanılabilir krediler"],
                ["GET", "/stores", "stores:read", "Bağlı mağazalar (salt okunur)"],
                ["POST", "/labels/validate", "labels:validate", "Kredi harcamadan doğrulama"],
                ["POST", "/labels", "labels:write", "Gönderi veya iade etiketi oluşturma"],
                ["GET", "/labels", "labels:read", "API etiketlerini listeleme"],
                ["GET", "/labels/{id}", "labels:read", "Etiket durumu ve takip no"],
                ["GET", "/labels/{id}/pdf", "labels:read", "Etiket PDF'i"],
                ["POST", "/labels/{id}/cancel", "labels:write", "Etiketi güvenle iptal etme ve krediyi geri yükleme"],
              ].map(([method, path, scope, detail]) => <tr key={`${method}-${path}`}><td className="px-4 py-3 font-semibold text-slate-950">{method}</td><td className="px-4 py-3"><code>{path}</code></td><td className="px-4 py-3"><code>{scope}</code></td><td className="px-4 py-3">{detail}</td></tr>)}
            </tbody>
          </table>
        </div>

        <div>
          <h3 className="mb-3 font-semibold text-slate-950">Etiket oluşturma örneği</h3>
          <CodeBlock onCopy={() => void copyText(curlExample, "cURL örneği kopyalandı.")}>{curlExample}</CodeBlock>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {(["outbound", "return"] as const).map(direction => <div key={direction}>
            <h3 className="mb-3 font-semibold text-slate-950">{pageLanguage === "de" ? (direction === "return" ? "Retouren mit eigenen Adressen" : "Versand mit eigenen Adressen") : pageLanguage === "en" ? (direction === "return" ? "Return with custom addresses" : "Outbound with custom addresses") : (direction === "return" ? "Özel adreslerle iade" : "Özel adreslerle normal gönderi")}</h3>
            <CodeBlock onCopy={() => void copyText(explicitAddressExample(direction), "cURL örneği kopyalandı.")}>{explicitAddressExample(direction)}</CodeBlock>
          </div>)}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {scenarioHelp.map(({ title, body }) => <InfoCard key={title} icon={<ShieldCheck className="h-5 w-5" />} title={title}><p>{body}</p></InfoCard>)}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <h3 className="mb-3 font-semibold text-slate-950">Başarılı yanıt</h3>
            <CodeBlock>{`{
  "id": "shp_...",
  "status": "ready",
  "trackingNumber": "003404...",
  "reference": "ORDER-10042",
  "shipmentType": "outbound",
  "pdfUrl": "${baseUrl}/labels/shp_.../pdf",
  "requestId": "req_..."
}`}</CodeBlock>
          </div>
          <div>
            <h3 className="mb-3 font-semibold text-slate-950">Standart hata</h3>
            <CodeBlock>{`{
  "error": {
    "code": "INSUFFICIENT_CREDIT",
    "message": "${insufficientCreditExample}",
    "details": null,
    "requestId": "req_..."
  }
}`}</CodeBlock>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-slate-200 p-4"><h3 className="flex items-center gap-2 font-semibold text-slate-950"><Clipboard className="h-4 w-4" /> Idempotency</h3><p className="mt-2 text-sm text-slate-600">Her etiket isteğinde sipariş ve paket için benzersiz bir anahtar gönderin. Aynı anahtar ve aynı gövde güvenle tekrar edilebilir.</p></div>
          <div className="rounded-xl border border-slate-200 p-4"><h3 className="flex items-center gap-2 font-semibold text-slate-950"><Clock3 className="h-4 w-4" /> Katmanlı hız sınırı</h3><p className="mt-2 text-sm text-slate-600">Anahtar, hesap ve etiket oluşturma limitlerinden biri aşılırsa <code>429</code> döner. <code>Retry-After</code> kadar bekleyin; otomatik tekrarlar için gecikmeyi kademeli artırın.</p></div>
          <div className="rounded-xl border border-slate-200 p-4"><h3 className="flex items-center gap-2 font-semibold text-slate-950"><ShieldCheck className="h-4 w-4" /> Veri sınırı</h3><p className="mt-2 text-sm text-slate-600">addresses ile iki adresi etiket bazında belirleyebilirsiniz. Bu alan gönderilmezse mevcut profil adresi kullanılır. API, hesap profilini veya mağaza bağlantılarını değiştirmez.</p></div>
        </div>
      </section>

      {showCreate ? (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
          <form onSubmit={createKey} className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div><h2 className="text-xl font-semibold text-slate-950">Yeni API key</h2><p className="mt-1 text-sm text-slate-500">Her uygulama veya sunucu için ayrı anahtar oluşturmanız önerilir.</p></div>
              <button type="button" onClick={() => setShowCreate(false)} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            <label className="mt-6 block text-sm font-semibold text-slate-700">Anahtar adı
              <input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} required className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-3 font-normal outline-none focus:border-slate-500" placeholder="Örn. Depo yazılımı" />
            </label>

            <fieldset className="mt-5"><legend className="text-sm font-semibold text-slate-700">Yetkiler</legend><div className="mt-2 grid gap-2 sm:grid-cols-2">
              {allScopes.map((scope) => {
                const checked = selectedScopes.includes(scope);
                return <label key={scope} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${checked ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}><input type="checkbox" checked={checked} onChange={() => setSelectedScopes((current) => checked ? current.filter((item) => item !== scope) : [...current, scope])} className="mt-1" /><span><span className="block text-sm font-semibold text-slate-800">{scopeDescriptions[scope].label}</span><span className="mt-0.5 block text-xs text-slate-500">{scopeDescriptions[scope].detail}</span><code className="mt-1 block text-[11px] text-slate-400">{scope}</code></span></label>;
              })}
            </div></fieldset>

            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <label className="text-sm font-semibold text-slate-700">Son kullanma tarihi (isteğe bağlı)<input type="date" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-3 font-normal outline-none focus:border-slate-500" /></label>
              <label className="text-sm font-semibold text-slate-700">Dakikalık istek sınırı<input type="number" value={rateLimit} onChange={(event) => setRateLimit(Number(event.target.value))} min={10} max={600} className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-3 font-normal outline-none focus:border-slate-500" /></label>
              <label className="text-sm font-semibold text-slate-700">Günlük etiket sınırı<input type="number" value={labelLimitPerDay} onChange={(event) => setLabelLimitPerDay(Number(event.target.value))} min={data?.limits.minLabelLimitPerDay || 1} max={data?.limits.maxLabelLimitPerDay || 2000} className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-3 font-normal outline-none focus:border-slate-500" /></label>
            </div>

            <label className="mt-5 block text-sm font-semibold text-slate-700">İzinli IP adresleri (isteğe bağlı)
              <textarea value={allowedIps} onChange={(event) => setAllowedIps(event.target.value)} rows={3} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 font-mono text-xs font-normal outline-none focus:border-slate-500" placeholder="203.0.113.10, 2001:db8::10" />
              <span className="mt-1 block text-xs font-normal text-slate-500">Boş bırakırsanız tüm IP adresleri kullanılabilir. Sabit sunucu IP'lerini virgülle ayırın.</span>
            </label>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setShowCreate(false)} className="h-11 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700">Vazgeç</button>
              <button type="submit" disabled={creating || selectedScopes.length === 0} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 text-sm font-semibold text-white disabled:opacity-50">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Anahtarı oluştur</button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
